import { Button, Grid, InputAdornment, TextField, Typography, Box, IconButton, Radio, InputLabel } from '@mui/material'
import React, { Fragment, useEffect, useState, useRef, useContext } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { StringsContext, useStrings } from '../../assets/localization/strings'
import { Service, VideoType } from '../../models/GeneralTypes'
import { numberToEuro } from '../../reusableUtils/Helpers'
import { usePricingContext } from '../../reusableUtils/pricing'
import { useCustomFieldArray } from '../../reusableUtils/useCustomFieldArray'
import { useReadonly } from '../../reusableUtils/useReadonly'
import { IRadioCardGroupOption } from '../common/form/ReactHookFormRadioCardGroupFullImage'
import type { IUGCCampaign } from './UGCCampaignForm'
import { formatPrice, getCurrencySymbolByCountry } from '../../reusableUtils/currency'
import rootStore from '../../stores/rootStore'
import TrashIcon from '../../assets/icons/trash_icon.svg'
import DownloadPDFIcon from '../../assets/icons/download_pdf_icon.svg'
import ReactHookFormRadioCardGroupFullImage from '../common/form/ReactHookFormRadioCardGroupFullImage'
import { ReactComponent as CheckedIcon } from '../../assets/icons/checked_icon.svg'
import  { RichTextEditor } from '../common/form/ReactHookFormWithRichText'

const Scenes = () => {
  const {
    control,
    formState: { errors }
  } = useFormContext<IUGCCampaign>()
  const { fields, update, append } = useCustomFieldArray({ control, name: 'scenes', keyName: 'id' })
  const isReadonly = useReadonly('scenes')
  const strings = useStrings()
  const { language } = useContext(StringsContext)

  const placeholders = [strings.campaign_creation_content_scenes_placeholder1]

  const add = () => append({ id: `${fields.length}`, value: '' })

  useEffect(() => {
    if (!fields.length) add()
  }, [JSON.stringify(fields)])

  return (
    <Grid container>
      {fields.map((field, i) => (
        <Grid item xs={12} mb={2} key={i}>
          <Box sx={{ position: 'relative' }}>
            <Controller
              name={`scenes.${i}.value`}
              control={control}
              defaultValue={field.value || ''}
              render={({ field: controllerField, fieldState: { error } }) => (
                <RichTextEditor
                  label=''
                  value={controllerField.value || ''}
                  onChange={value => {
                    controllerField.onChange(value)
                    // Optional: keep your custom update if needed for other logic
                    update(i, { ...field, value })
                  }}
                  onBlur={controllerField.onBlur}
                  placeholder={placeholders[i] ?? strings.campaign_creation_content_scene_placeh}
                  error={!!error}
                  helperText={error?.message || ''}
                  disabled={isReadonly}
                  toolbar={true}
                  minHeight={120}
                  maxHeight={300}
                />
              )}
            />

            <IconButton
              component='a'
              href={
                language.startsWith('en')
                  ? 'https://s3.eu-central-1.amazonaws.com/com.freebeeapp/public/UGC_Skript_stylink_UK.pdf'
                  : 'https://s3.eu-central-1.amazonaws.com/com.freebeeapp/public/UGC_Skript_stylink_DE.pdf'
              }
              target='_blank'
              download
              sx={{
                position: 'absolute',
                top: -40,
                right: 8,
                '&:hover': {
                  backgroundColor: 'transparent'
                },
                '&:active': { backgroundColor: 'transparent' }
              }}>
              <label style={{ fontSize: '12px', fontWeight: 400 }}>{strings.campaign_creation_sceens_example}</label>
              <img src={DownloadPDFIcon} alt='pdf download icon' width={'50px'} height={'50px'} />
            </IconButton>
          </Box>
        </Grid>
      ))}
    </Grid>
  )
}

export const ContentOptions = ({ optionName, data }) => {
  const {
    control,
    formState: { errors },
    setValue
  } = useFormContext<IUGCCampaign>()

  const { brandId, campaignId, type } = useParams<{ brandId: string; campaignId: string; type: 'update' | 'duplicate' | 'view'; couponCode?: string }>()

  const fieldArrayName = optionName.includes('Hooks') ? 'hooks' : 'ctas'
  const { fields, append, remove, update } = useCustomFieldArray({
    control,
    name: fieldArrayName,
    keyName: 'id'
  })

  // Initialize selected option based on existing field count
  const getInitialSelectedOption = count => {
    if (count === 0) return 1
    if (count === 1) return 1
    if (count === 2) return 2
    return 3
  }

  const [selectedHooksOption, setSelectedHooksOption] = useState(getInitialSelectedOption(fields.length))
  const isReadonly = useReadonly(fieldArrayName)
  const strings = useStrings()
  const placeholders = fieldArrayName === 'hooks' ? [strings.campaign_creation_content_hooks_placeholder1] : [strings.campaign_creation_content_ctas_placeholder1]
  const pricing = usePricingContext()
  const currencySymbol = rootStore.brandStore.selectedBrand?.brandCountry ? getCurrencySymbolByCountry(rootStore.brandStore.selectedBrand?.brandCountry) : '€'

  // Store a reference to detect first mount
  const isInitialMount = useRef(true)

  // Ensure at least one field exists on mount when not readonly
  useEffect(() => {
    if (!isReadonly && fields.length === 0) {
      append({ id: `default-${Date.now()}`, value: '' })
    }
  }, [isReadonly, append, fields.length])

  // Update the form value to match actual field count
  useEffect(() => {
    setValue(optionName, fields.length)
  }, [fields.length, setValue, optionName])

  // Handle option selection changes
  useEffect(() => {
    // Skip on first mount to avoid duplicate initialization
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    if (isReadonly) return

    // Ensure field count matches selected option
    const targetCount = selectedHooksOption === 1 ? 1 : selectedHooksOption === 2 ? 2 : 3 // Option 3 means at least 3 fields

    if (fields.length < targetCount) {
      // Add more fields
      for (let i = fields.length; i < targetCount; i++) {
        append({ id: `option-${Date.now()}-${i}`, value: '' })
      }
    } else if (fields.length > targetCount && selectedHooksOption !== 3) {
      // Start removing from the highest index
      for (let i = fields.length - 1; i >= targetCount; i--) {
        remove(i)
      }
    }
  }, [selectedHooksOption, isReadonly, fields.length, append, remove])

  // Add hook function
  const addHook = () => {
    if (fields.length < 5) {
      append({ id: `add-${Date.now()}`, value: '' })

      // Update selected option based on new field count
      const newCount = fields.length + 1
      setSelectedHooksOption(getInitialSelectedOption(newCount))
    }
  }

  const removeHook = i => {
    remove(i)

    // Update selected option based on new field count
    const newCount = fields.length - 1
    setSelectedHooksOption(getInitialSelectedOption(newCount))
  }

  // Render the three option cards
  const renderOptions = () => (
    <Box display='flex' justifyContent='space-between' gap={2} mb={4}>
      {data.map(({ value, title, description, price }, index) => (
        <Box
          key={index}
          onClick={() => !isReadonly && setSelectedHooksOption(value)}
          sx={{
            flex: 1,
            border: '1px solid',
            borderColor: selectedHooksOption === value ? '#AB95FF' : '#ccc',
            borderRadius: '8px',
            p: 2,
            textAlign: 'left',
            cursor: isReadonly ? 'default' : 'pointer',
            position: 'relative',
            backgroundColor: selectedHooksOption === value ? '#AB95FF1A' : '#fff'
          }}>
          <Radio checked={selectedHooksOption === value} checkedIcon={<CheckedIcon />} sx={{ position: 'absolute', right: '0px', top: '0px' }} value={value} />
          <Typography fontWeight={600} fontSize='18px'>
            {title}
          </Typography>
          <Grid container xs={12} sx={{ display: 'flex', flexDirection: 'row' }}>
            <Typography variant='body2' mt={1} fontWeight={400} fontSize='14px' width={'60%'}>
              {description}
            </Typography>
            <Grid item sx={{ display: 'flex', flexDirection: 'row', width: '40%' }}>
              <div style={{ borderStyle: 'solid', borderColor: '#17181B40', borderWidth: '0.5px', height: '95%', marginRight: '12px' }} />

              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'left' }}>
                <label style={{ fontSize: '14px', fontWeight: 400, color: '#17181B' }}>{strings.price}</label>
                <span style={{ fontSize: '20px', fontWeight: 600 }}>{typeof price === 'string' ? strings.inclusive : formatPrice(price.toFixed(2), currencySymbol)}</span>
              </div>
            </Grid>
          </Grid>
        </Box>
      ))}
    </Box>
  )
  return (
    <Grid item xs={12} my={2} sx={{ borderStyle: 'solid', borderRadius: '16px', borderWidth: '1px', borderColor: '#DADCE0', padding: '14px' }}>
      {optionName === 'numberOfHooks' && <Typography sx={{ fontSize: '16px', fontWeight: 400, mb: '30px' }}>{strings.campaign_creation_intro_hook_desc}</Typography>}
      {renderOptions()}

      {fields.map((field, i) => (
        <Grid container mt={2} alignItems='center' key={field.id || i}>
          <Grid item xs={11}>
            <TextField
              multiline
              value={field.value || ''}
              onChange={e => update(i, { ...field, value: e.target.value })}
              label={fieldArrayName === 'hooks' ? strings.campaign_creation_content_hooks_placeh(i + 1) : strings.campaign_creation_content_ctas_placeh(i + 1)}
              variant='outlined'
              fullWidth
              size='small'
              placeholder={
                placeholders[i] ?? (fieldArrayName === 'hooks' ? strings.campaign_creation_content_hooks_placeh(i + 1) : strings.campaign_creation_content_ctas_placeh(i + 1))
              }
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: <InputAdornment position='start'>{i + 1}.</InputAdornment>
              }}
              error={!!errors.hooks?.[i]?.value}
              helperText={errors.hooks?.[i]?.value?.message || ''}
              disabled={type === 'view'}
              name={`hook${i + 1}`}
            />
          </Grid>
          {!isReadonly && fields.length > 1 && (
            <div
              style={{
                backgroundColor: '#FF2B001A',
                padding: '8px',
                marginLeft: '8px',
                borderRadius: '10px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer'
              }}
              onClick={() => removeHook(i)}>
              <img src={TrashIcon} alt='delete icon' />
            </div>
          )}
        </Grid>
      ))}

      {!isReadonly && fields.length < 5 && (
        <Grid item xs={12} textAlign='center' mt={2}>
          <Button variant='outlined' onClick={addHook}>
            {fieldArrayName === 'hooks'
              ? strings.campaign_creation_content_hooks_add_button(currencySymbol, numberToEuro(pricing.hook))
              : strings.campaign_creation_content_ctas_add_button(currencySymbol, numberToEuro(pricing.cta))}
          </Button>
        </Grid>
      )}
    </Grid>
  )
}

// deprecated in favor of ContentOptions component due to the identical design
// const Ctas = () => {
//   const {
//     control,
//     formState: { errors },
//     setValue
//   } = useFormContext<IUGCCampaign>()
//   const { fields, append, remove, update } = useCustomFieldArray({ control, name: 'ctas', keyName: 'id' })
//   const isReadonly = useReadonly('ctas')
//   const strings = useStrings()
//   const placeholders = [strings.campaign_creation_content_ctas_placeholder1]
//   const pricing = usePricingContext()
//   const currencySymbol = rootStore.brandStore.selectedBrand?.brandCountry ? getCurrencySymbolByCountry(rootStore.brandStore.selectedBrand?.brandCountry) : '€'

//   const add = () => append({ id: `${fields.length}`, value: '' })

//   useEffect(() => {
//     if (!fields.length) add()
//     setValue('numberOfCtas', fields.length)
//   }, [JSON.stringify(fields)])

//   return (
//     <Grid item xs={12} mt={2}>
//       {fields.map((field, i) => (
//         <Grid container mt={2} alignItems='center' key={i}>
//           <Grid item xs={11}>
//             <TextField
//               multiline
//               value={field.value}
//               onChange={e => update(i, { ...field, value: e.target.value })}
//               label={strings.campaign_creation_content_ctas_placeh(i + 1)}
//               type='outlined'
//               fullWidth
//               size='small'
//               placeholder={placeholders[i] ?? strings.campaign_creation_content_ctas_placeh(i + 1)}
//               InputLabelProps={{ shrink: true }}
//               InputProps={{ startAdornment: <InputAdornment position='start'>{i + 1}.</InputAdornment> }}
//               error={!!errors.ctas}
//               helperText={errors.ctas?.[i]?.value?.message || ''}
//               name={`cta${i + 1}`}
//             />
//           </Grid>
//           {!isReadonly && fields.length > 1 && (
//             <Grid item xs={1} textAlign='center'>
//               <DeleteIcon fontSize='small' onClick={() => remove(i)} />
//             </Grid>
//           )}
//         </Grid>
//       ))}
//       {!isReadonly && fields.length < 5 && (
//         <Grid item xs={12} textAlign='center' mt={2}>
//           <Button variant='outlined' onClick={add}>
//             {strings.campaign_creation_content_ctas_add_button(currencySymbol, numberToEuro(pricing.cta))}
//           </Button>
//         </Grid>
//       )}
//     </Grid>
//   )
// }

const CampaignTask = () => {
  const { control, setValue } = useFormContext<IUGCCampaign>()
  const { type } = useParams<{ type: 'update' | 'view' }>()
  const pricing = usePricingContext()
  const strings = useStrings()
  const videoType = useWatch({ control, name: 'videoType' })
  const service = useWatch({ control, name: 'service' })
  const optionsService: IRadioCardGroupOption[] = [
    {
      value: Service.nanoService,
      title: strings.campaign_creation_content_nano_storyboard_label,
      description: strings.campaign_creation_content_nano_storyboard_desc,
      price: pricing[Service.nanoService]
    },
    {
      value: Service.selfService,
      title: strings.campaign_creation_content_self_service_label,
      description: strings.campaign_creation_content_self_service_desc,
      price: 0
    }
  ].filterNotNull()

  const hooksData = [
    {
      value: 1,
      title: '1 Hook',
      description: strings.campaign_creation_hook_one_desc,
      price: strings.inclusive
    },
    {
      value: 2,
      title: '+1 Hook',
      description: strings.campaign_creation_hook_two_desc,
      price: 29
    },
    {
      value: 3,
      title: '+2 Hook',
      description: strings.campaign_creation_hook_three_desc,
      price: 58
    }
  ]

  const showService = videoType == VideoType.ad || videoType == VideoType.premium
  const showScenes = showService && (service == Service.selfService || (service == Service.nanoService && (type == 'update' || type == 'view')))
  const isUnboxing = videoType === VideoType.unboxing

  useEffect(() => {
    if ((videoType == VideoType.premium || videoType == VideoType.ad) && type != 'update' && type != 'view') setValue('service', Service.selfService)
  }, [videoType])
  return (
    <Grid container>
      {showService && (
        <Grid item xs={12}>
          <Typography sx={{ fontSize: '18px', fontWeight: 500, color: '#190851', fontFamily: 'Bricolage Grotesque', mb: '12px' }}>Storyboard</Typography>
          <ReactHookFormRadioCardGroupFullImage name='service' options={optionsService} md={6} required />
        </Grid>
      )}

      <>
        {!isUnboxing && (
          <Fragment>
            <Grid item xs={12} mt={2}>
              <Typography variant='h3' sx={{ fontSize: '18px', fontFamily: 'Bricolage Grotesque', fontWeight: 600, color: '#190851', textAlign: 'left', mt: '16px' }}>
                {strings.campaign_creation_content_hooks_title}
              </Typography>
            </Grid>
            <ContentOptions optionName='numberOfHooks' data={hooksData} />
          </Fragment>
        )}

        {/* Scenes section */}
        {(showScenes || isUnboxing) && (
          <>
            <Grid item xs={12} mb={2}>
              <Typography variant='h3' sx={{ fontSize: '18px', fontFamily: 'Bricolage Grotesque', fontWeight: 600, color: '#190851', textAlign: 'left', mt: '16px' }}>
                {strings.campaign_creation_content_scenes_title}
              </Typography>
            </Grid>
            <Scenes />
          </>
        )}
      </>

      {/* Additional info Section deprecated */}

      {/* {!isUnboxing && (
        <Fragment>
          <Grid item xs={12} mt={showScenes || showService || isUnboxing ? 4 : 0}>
            <Typography variant='h5'>{mentionsTitle}</Typography>
          </Grid>
          <Grid item xs={12} mt={2}>
            <Typography variant='body2'>{mentionsDesc}</Typography>
          </Grid>
          <Grid item xs={12} mt={2}>
            {fields.map((field, i) => (
              <Grid container mt={2} alignItems='center' key={i}>
                <Grid item xs={11}>
                  <TextField
                    value={field.value}
                    onChange={e => {
                      update(i, { ...field, value: e.target.value })
                      // trigger only when necessary to avoid lags
                      if (e.target.value.length < 120 != (field.value || '').length < 120) trigger(`mentions.${i}`)
                    }}
                    label={mentionsLabel(i + 1)}
                    error={!!formState.errors.mentions?.[i]}
                    helperText={formState.errors.mentions?.[i]?.value?.message}
                    type='outlined'
                    fullWidth
                    size='small'
                    disabled={isReadonly}
                  />
                </Grid>
                {!isReadonly && fields.length > 1 && (
                  <Grid item xs={1} textAlign='center'>
                    <DeleteIcon fontSize='small' onClick={() => remove(i)} />
                  </Grid>
                )}
              </Grid>
            ))}
            {!isReadonly && fields.length < 5 && (
              <Grid item xs={12} textAlign='center' mt={2}>
                <Fab color='primary' aria-label='add' size='small' onClick={() => append({ id: `${fields.length}`, value: '' })}>
                  <AddIcon />
                </Fab>
              </Grid>
            )}
          </Grid>
        </Fragment>
      )} */}
    </Grid>
  )
}

export default CampaignTask
